import React from 'react';
import styled, { keyframes } from 'styled-components';
import Card from 'components/Card';
import ContentWrapper from 'components/ContentWrapper';
import {
  DashboardLayout,
  MainContent,
  Sidebar,
} from 'components/DashboardLayout';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import PageContainer from 'components/PageContainer';
import { HeaderContainer } from 'components/PageHeader';
import TableSkeleton from 'components/TableSkeleton';

// Shimmer animation
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const SkeletonBase = styled.div`
  background: ${(props) =>
    props.theme.name === 'dark'
      ? 'linear-gradient(90deg, rgba(255,255,255,0.03) 0%, rgba(255,255,255,0.08) 50%, rgba(255,255,255,0.03) 100%)'
      : 'linear-gradient(90deg, rgba(0,0,0,0.04) 0%, rgba(0,0,0,0.08) 50%, rgba(0,0,0,0.04) 100%)'};
  background-size: 200% 100%;
  animation: ${shimmer} 1.5s ease-in-out infinite;
  border-radius: ${(props) => props.radius || '4px'};
`;

const SkeletonIcon = styled(SkeletonBase)`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  flex-shrink: 0;
`;

const SkeletonTitle = styled(SkeletonBase)`
  height: 24px;
  width: 200px;
  margin-bottom: 8px;
`;

const SkeletonSubtitle = styled(SkeletonBase)`
  height: 16px;
  width: 280px;
`;

const SkeletonLogo = styled(SkeletonBase)`
  width: 80px;
  height: 80px;
  border-radius: 8px;
  flex-shrink: 0;
  margin-left: auto;
`;

const SkeletonSearchBar = styled(SkeletonBase)`
  height: 44px;
  flex: 1;
  border-radius: 8px;
`;

const SkeletonButton = styled(SkeletonBase)`
  height: 44px;
  width: 140px;
  border-radius: 8px;
  flex-shrink: 0;
`;

const ToolbarRow = styled.div`
  display: flex;
  gap: 12px;
  align-items: center;
  margin-bottom: 24px;
`;

const TabRow = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
`;

const SkeletonTab = styled(SkeletonBase)`
  height: 48px;
  width: 160px;
  border-radius: 8px;
`;

const SkeletonQuickAction = styled(SkeletonBase)`
  height: 44px;
  width: 100%;
  border-radius: 8px;
`;

const QuickActionsCard = styled(Card)`
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const QuickActionsTitle = styled(SkeletonBase)`
  height: 16px;
  width: 100px;
  margin-bottom: 8px;
`;

const TrackEventGuestListSkeleton = () => {
  return (
    <EnhancedThemeProvider>
      <PageContainer>
        {/* Header Card */}
        <Card style={{ marginBottom: '30px' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <HeaderContainer>
              <SkeletonIcon />
              <div>
                <SkeletonTitle />
                <SkeletonSubtitle />
              </div>
            </HeaderContainer>
            <SkeletonLogo />
          </div>
        </Card>

        {/* Dashboard Layout */}
        <DashboardLayout>
          <MainContent>
            <Card noPadding>
              <ContentWrapper>
                {/* Toolbar */}
                <ToolbarRow>
                  <SkeletonSearchBar />
                  <SkeletonButton />
                </ToolbarRow>

                {/* Tabs */}
                <TabRow>
                  <SkeletonTab />
                  <SkeletonTab />
                </TabRow>

                {/* Table */}
                <TableSkeleton
                  rows={8}
                  columns={3}
                  showActions
                  actionsCount={1}
                />
              </ContentWrapper>
            </Card>
          </MainContent>

          <Sidebar>
            {/* Quick Actions */}
            <QuickActionsCard>
              <QuickActionsTitle />
              <SkeletonQuickAction />
              <SkeletonQuickAction />
              <SkeletonQuickAction />
            </QuickActionsCard>
          </Sidebar>
        </DashboardLayout>
      </PageContainer>
    </EnhancedThemeProvider>
  );
};

export default TrackEventGuestListSkeleton;
